import React from "react";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useTheme, alpha } from "@mui/material/styles";

import BoltIcon from "@mui/icons-material/Bolt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Instructions from "../../components/Instructions";
import { getShortcutBySlug } from "./shortcutData";

export default function ShortcutDetail() {
    const theme = useTheme();
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const shortcut = slug ? getShortcutBySlug(slug) : undefined;

    if (!shortcut) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    background: "#ffffff",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                }}
            >
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Shortcut not found
                </Typography>
                <Button
                    component={RouterLink}
                    to="/shortcuts"
                    variant="contained"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                >
                    Back to Shortcuts
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: "#ffffff",
            }}
        >
            {/* Navigation Bar - match Landing */}
            <Box
                sx={{
                    pt: 5,
                    px: { xs: 0, sm: 4 },
                    backgroundColor: "#ffffff",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            component={RouterLink}
                            to="/"
                            sx={{ display: "inline-block" }}
                        >
                            <Box
                                component="img"
                                src="/tapsheet_logo_text.png"
                                alt="TapSheet"
                                sx={{
                                    height: { xs: "24px", sm: "28px" },
                                    width: "auto",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                        <Button
                            onClick={() => navigate("/shortcuts")}
                            startIcon={<ArrowBackIcon />}
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                px: { xs: 1.5, sm: 2 },
                            }}
                        >
                            Shortcuts
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Content */}
            <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 }, pb: 6 }}>
                <Stack spacing={3}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <BoltIcon
                                    sx={{
                                        color: theme.palette.primary.main,
                                        fontSize: 28,
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                                }}
                            >
                                {shortcut.name}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6, pl: { xs: 0, sm: 7 } }}
                        >
                            {shortcut.description}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 2, fontSize: "1rem" }}
                        >
                            Get the shortcut
                        </Typography>
                        <Button
                            href={shortcut.importUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<BoltIcon />}
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: 1.5,
                                fontSize: "0.95rem",
                                fontWeight: 600,
                            }}
                        >
                            Import Shortcut
                        </Button>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 1.5 }}
                        >
                            Opens iCloud Shortcuts. Open the link on your iPhone to add the shortcut.
                        </Typography>
                    </Box>

                    <Instructions
                        title={shortcut.instructionsTitle}
                        steps={shortcut.steps}
                    />
                </Stack>
            </Container>

            {/* Footer - match Landing */}
            <Box
                sx={{
                    py: 3,
                    px: { xs: 2, sm: 4 },
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: "background.paper",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 3,
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            component={RouterLink}
                            to="/privacy"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                            }}
                        >
                            Privacy Policy
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/terms"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                            }}
                        >
                            Terms of Service
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/support"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                            }}
                        >
                            Support
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
